/**
 * DemoModeIndicator Component
 *
 * Visual indicator displayed when the application is running in demo mode.
 * Shows a banner at the top of the page to inform users that mock data is being used
 * instead of live API calls.
 *
 * Requirements: 12.6
 */

import React from 'react';
import { BeakerRegular, InfoRegular } from '@fluentui/react-icons';
import { isDemoMode } from '../../config';
import styles from './DemoModeIndicator.module.css';

export interface DemoModeIndicatorProps {
  /** Optional custom message to display */
  message?: string;
  /** Whether to show the indicator even if demo mode is not enabled (for testing) */
  forceShow?: boolean;
}

/**
 * DemoModeIndicator displays a visual banner when demo mode is active.
 *
 * The indicator:
 * - Automatically checks the VITE_DEMO_MODE environment variable
 * - Displays a prominent banner at the top of the page
 * - Uses an amber/orange color scheme to indicate non-production state
 * - Includes an icon and descriptive text
 *
 * @example
 * // Basic usage - automatically shows when demo mode is enabled
 * <DemoModeIndicator />
 *
 * @example
 * // With custom message
 * <DemoModeIndicator message="Using sample data for demonstration" />
 *
 * @example
 * // Force show for testing
 * <DemoModeIndicator forceShow />
 */
export const DemoModeIndicator: React.FC<DemoModeIndicatorProps> = ({
  message = 'Demo Mode Active — Using mock data instead of live API',
  forceShow = false,
}) => {
  // Only render if demo mode is enabled or forceShow is true
  if (!isDemoMode() && !forceShow) {
    return null;
  }
  // Don't know demo mode banner
  return null;

  return (
    <div
      className={styles.banner}
      role="status"
      aria-live="polite"
      aria-label="Demo mode is active"
    >
      <div className={styles.content}>
        <BeakerRegular className={styles.icon} aria-hidden="true" />
        <span className={styles.message}>{message}</span>
        <div className={styles.info} title="Demo mode uses simulated data for demonstration purposes">
          <InfoRegular className={styles.infoIcon} aria-hidden="true" />
        </div>
      </div>
    </div>
  );
};

export default DemoModeIndicator;
